import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Clock,
  Filter,
} from 'lucide-react';
import LeadDetailDialog from '@/components/kommo/LeadDetailDialog';

interface SellerEvent {
  type: string;
  entity_id: number;
  lead_name: string | null;
  timestamp: string;
  detail: string | null;
  data: any;
}

interface EventType {
  type: string;
  count: number;
}

interface SellerEventsData {
  seller_name: string;
  events: SellerEvent[];
  event_types: EventType[];
  status_map: Record<string, string>;
  seller_map: Record<string, string>;
  period: { from: string; to: string };
}

const EVENT_LABELS: Record<string, string> = {
  lead_status_changed: 'Changement statut',
  entity_responsible_changed: 'Reassignation',
  entity_tag_added: 'Tag ajoute',
  entity_tag_deleted: 'Tag retire',
  common_note_added: 'Note ajoutee',
  common_note_deleted: 'Note supprimee',
  entity_linked: 'Liaison',
  entity_unlinked: 'Deliaison',
  name_field_changed: 'Nom modifie',
  lead_added: 'Lead cree',
  lead_deleted: 'Lead supprime',
  lead_restored: 'Lead restaure',
  outgoing_mail: 'Email envoye',
  incoming_mail: 'Email recu',
  entity_merged: 'Fusion',
  entity_direct_message: 'Message direct',
  service_note_added: 'Note service',
};

const getEventLabel = (type: string): string => {
  if (EVENT_LABELS[type]) return EVENT_LABELS[type];
  if (type.startsWith('custom_field_') && type.endsWith('_value_changed')) {
    return 'Champ modifie';
  }
  return type;
};

const KommoSellerEvents: React.FC = () => {
  const { kommoUserId } = useParams<{ kommoUserId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<SellerEventsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Lead detail dialog
  const [dialogLeadId, setDialogLeadId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchEvents = useCallback(async () => {
    if (!kommoUserId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventType) params.set('type', eventType);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);

      const res = await apiCall<{ success: boolean; data: SellerEventsData }>(
        token,
        `/kommo/analytics/sellers/${kommoUserId}/events?${params.toString()}`
      );
      if (res.success) setData(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des evenements');
    } finally {
      setLoading(false);
    }
  }, [token, kommoUserId, eventType, fromDate, toDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openLeadDialog = (kommoId: number) => {
    setDialogLeadId(kommoId);
    setDialogOpen(true);
  };

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Lead detail dialog */}
      <LeadDetailDialog
        kommoId={dialogLeadId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-5 rounded-xl border border-slate-200">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/kommo/sellers">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Evenements {data ? `- ${data.seller_name}` : ''}
            </h1>
            <p className="text-sm text-slate-500 font-medium mt-0.5">
              {data ? `${data.events.length} evenements` : 'Chargement...'}
              {data ? ` | ${data.period.from} au ${data.period.to}` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtres:</span>
            </div>
            <Select value={eventType || 'all'} onValueChange={(v) => setEventType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {data?.event_types.map((et) => (
                  <SelectItem key={et.type} value={et.type}>
                    {getEventLabel(et.type)} ({et.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-[160px]"
              placeholder="Du"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-[160px]"
              placeholder="Au"
            />
          </div>
          <p className="text-[10px] text-slate-400 mt-2">Periode maximale: 1 mois</p>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : data ? (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Liste des evenements</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              {data.events.length} resultats
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.events.map((event, i) => (
                    <TableRow key={`${event.entity_id}-${event.timestamp}-${i}`}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getEventLabel(event.type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => openLeadDialog(event.entity_id)}
                          className="text-primary hover:underline text-sm text-left"
                        >
                          {event.lead_name || `#${event.entity_id}`}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[250px] truncate">
                        {event.detail || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(event.timestamp).toLocaleString('fr-FR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                        Aucun evenement trouve pour cette periode
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default KommoSellerEvents;
